import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageProvider";
import { COMPANY } from "@/lib/brand";
import { submitContact } from "@/lib/api";
import { Mail, MapPin, Clock, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
    gdpr: false,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr) {
      toast.error(t.contact.form.gdpr);
      return;
    }
    setLoading(true);
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone || null,
        message: form.message,
        gdpr_consent: true,
        language: lang,
      });
      toast.success(t.contact.form.success);
      setForm({ name: "", company: "", email: "", phone: "", message: "", gdpr: false });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  // Munich Elsenheimerstraße 15 approx
  const mapSrc = `https://www.openstreetmap.org/export/embed.html?bbox=11.520,48.135,11.545,48.148&layer=mapnik&marker=${COMPANY.hq.lat},${COMPANY.hq.lng}`;

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative scroll-mt-24 overflow-hidden bg-[#F8FAFC] py-24 dark:bg-[#0B1020] md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#1E3A8A]/15 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1E3A8A] dark:border-white/10 dark:bg-white/5 dark:text-[#A5B4FC]">
            {t.contact.eyebrow}
          </div>
          <h2 className="mt-5 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-tight tracking-tight text-[#0F172A] dark:text-white">
            {t.contact.title}
          </h2>
          <p className="mt-4 max-w-xl text-base text-slate-600 dark:text-slate-300 md:text-lg">
            {t.contact.subtitle}
          </p>
        </motion.div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.2fr_1fr]">
          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-slate-200/70 bg-white p-7 shadow-elevated md:p-10 dark:border-white/5 dark:bg-[#0F172A]"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                required
                data-testid="contact-name"
                placeholder={t.contact.form.name}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-12 rounded-xl"
              />
              <Input
                data-testid="contact-company"
                placeholder={t.contact.form.company}
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="h-12 rounded-xl"
              />
              <Input
                required
                type="email"
                data-testid="contact-email"
                placeholder={t.contact.form.email}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-12 rounded-xl"
              />
              <Input
                type="tel"
                data-testid="contact-phone"
                placeholder={t.contact.form.phone}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="h-12 rounded-xl"
              />
              <div className="sm:col-span-2">
                <Textarea
                  required
                  data-testid="contact-message"
                  placeholder={t.contact.form.message}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={6}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="mt-5 flex items-start gap-3">
              <Checkbox
                id="contact-gdpr"
                checked={form.gdpr}
                onCheckedChange={(v) => setForm({ ...form, gdpr: !!v })}
                data-testid="gdpr-consent-checkbox"
                className="mt-0.5"
              />
              <label htmlFor="contact-gdpr" className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                {t.contact.form.gdpr}
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-contact-button"
              className="mt-6 inline-flex h-12 items-center gap-2 rounded-xl bg-gradient-to-r from-[#1E3A8A] to-[#7C3AED] px-6 text-white shadow-glow-purple transition-all hover:scale-[1.02]"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {t.contact.form.submit}
                  <Send className="h-4 w-4" />
                </>
              )}
            </Button>
          </motion.form>

          {/* Info + map */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-5"
          >
            <div className="rounded-3xl border border-slate-200/70 bg-white p-6 dark:border-white/5 dark:bg-[#0F172A]">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#7C3AED] text-white">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {t.contact.address_title}
                  </div>
                  <div className="mt-1 font-display font-bold text-[#0F172A] dark:text-white">
                    {COMPANY.legal}
                  </div>
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    {COMPANY.street}<br />
                    {COMPANY.zip} {COMPANY.city}<br />
                    {COMPANY.country}
                  </div>
                </div>
              </div>
              <div className="my-5 h-px bg-slate-200 dark:bg-white/5" />
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-sm font-medium text-[#1E3A8A] hover:text-[#7C3AED] dark:text-[#A5B4FC]"
              >
                <Mail className="h-4 w-4" />
                {COMPANY.email}
              </a>
              <div className="mt-3 flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
                <Clock className="h-4 w-4" />
                {t.contact.hours_value}
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-elevated dark:border-white/5">
              <iframe
                title="Genius WorkPA Hauptsitz"
                src={mapSrc}
                className="h-[280px] w-full"
                style={{ border: 0 }}
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
